import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PublicIcon from '@mui/icons-material/Public';

import { debugLog } from "../../functions";
import { PERMISSIONS } from "../../constants";
import DialogConfirmation from "./DialogConfirmation";

const ProcessesCard = ({ process, onCardClick }) => {
  const user = useSelector((state) => state.user.value);
  const [menuState, setMenuState] = useState(null);
  const [archiveConfirmInfo, setArchiveConfirmInfo] = useState({ open: false });
  const [deleteConfirmInfo, setDeleteConfirmInfo] = useState({ open: false });

  const handleDelete = () => {
    setMenuState(null);
    setDeleteConfirmInfo({
      open: true,
      title: `¿Realmente deseas eliminar el proceso "${process?.processName}"?`,
    });
  };

  const handleCreate = () => {
    onCardClick(process, "config");
    setMenuState(null);
  };

  const handleEdit = () => {
    onCardClick(process, "edit");
    setMenuState(null);
  };

  const handleArchive = () => {
    setMenuState(null);
    setArchiveConfirmInfo({
      open: true,
      title: process?.status === "archived"
        ? `¿Realmente deseas activar el proceso "${process?.processName}"?`
        : `¿Realmente deseas archivar el proceso "${process?.processName}"?`,
    });
  };

  const cardStyle = {
    width: "60mm",
    height: "84mm",
    margin: "auto",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between", // Asegura que los elementos se alineen al inicio y al final
    transition: "background-color 0.3s", // Suaviza la transición del color de fondo
    cursor: "pointer",
  };

  const actionAreaStyle = {
    height: "100%", // Hace que CardActionArea ocupe toda la altura de la tarjeta
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  // Ajuste de estilo para que el título aparezca en dos líneas
  const titleStyle = {
    fontSize: "4.5mm",
    fontWeight: "bold",
    lineHeight: "1.2", // Aumenta el interlineado para que las dos líneas no estén muy juntas
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "flex", // Usa flex para permitir el control del espacio interno
    flexDirection: "column", // Organiza los elementos secundarios en una columna
    justifyContent: "center", // Centra el texto verticalmente
    alignItems: "center", // Centra el contenido horizontalmente
    textAlign: "center", // Asegura que el texto esté centrado horizontalmente
    height: "9mm", // Establece una altura fija que pueda contener dos líneas
    margin: "2mm 0", // Agrega margen superior e inferior si es necesario
    padding: "0 1mm", // Asegúrate de tener algo de espacio en los lados
    whiteSpace: "pre-wrap", // Permite que el texto se ajuste y respete los saltos de línea
    wordWrap: "break-word", // Asegura que las palabras se rompan si es necesario
  };
  const bodyTextStyle = {
    fontSize: "4mm",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  // Asegúrese de que el footer esté pegado a la parte inferior de la tarjeta
  const footerStyle = {
    position: "absolute", // Posicionamiento absoluto para pegarlo al fondo
    fontWeight: "bold",
    bottom: "1mm", // Espacio desde el borde inferior de la tarjeta
    left: "0",
    right: "0",
    display: "flex",
    justifyContent: "space-between", // Espaciado entre fecha y código
    alignItems: "center", // Centra los elementos verticalmente
    fontSize: "3.5mm", // Ajustar el tamaño de fuente si es necesario
    padding: "0 1mm", // Añade padding horizontal para evitar desbordamiento
  };

  const handleMenuOpen = (event, newProcess) => {
    setMenuState(event.currentTarget);
    event.stopPropagation(); // Evitar que se propague el evento de clic
  };

  return (
    <>
      <Card style={cardStyle}>
        <CardActionArea
          style={actionAreaStyle}
          onMouseOver={(event) =>
            (event.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseOut={(event) =>
            (event.currentTarget.style.backgroundColor = "")
          }
          onClick={() => onCardClick(process, "config")}
        >
          <CardContent style={{ position: "relative", height: "100%", width: "80%" }}>
            <IconButton
              style={{ position: "absolute", top: "4px", right: "4px" }}
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, process);
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Typography style={titleStyle}>{process?.processName}</Typography>
            <Typography variant="body2" style={bodyTextStyle}>
              {process?.description}
            </Typography>
            <div style={footerStyle}>
              <Typography>{process?.qualityCode}</Typography>
              {process.isPublic && <PublicIcon color='success' />}
            </div>
          </CardContent>
        </CardActionArea>
      </Card>
      <Menu
        anchorEl={menuState}
        open={Boolean(menuState)}
        onClose={(evt) => setMenuState(null)}
      >
        <MenuItem onClick={handleEdit}>Editar</MenuItem>
        <MenuItem onClick={handleCreate}>Instanciar</MenuItem>
        <MenuItem
          onClick={handleArchive}
          disabled={!user.permissions.includes(PERMISSIONS.PROCESS.DELETE) || process.userId !== user.userId}
        >
          {process?.status === "archived" ? "Activar" : "Archivar"}
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          disabled={!user.permissions.includes(PERMISSIONS.PROCESS.DELETE) || process.userId !== user.userId}
        >
          Eliminar
        </MenuItem>
      </Menu>
      <DialogConfirmation
        confirmInfo={archiveConfirmInfo}
        handleCloseDialog={(result) => {
          if (result) onCardClick(process, "archive");
          setArchiveConfirmInfo({ open: false });
        }}
      />
      <DialogConfirmation
        confirmInfo={deleteConfirmInfo}
        handleCloseDialog={(result) => {
          if (result) onCardClick(process, "delete");
          setDeleteConfirmInfo({ open: false });
        }}
      />
    </>
  );
};

export default ProcessesCard;
